import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="searchable-select"
export default class extends Controller {
  static targets = [
    "input",
    "dropdown",
    "options",
    "hiddenField",
    "displayValue",
  ];

  connect() {
    this.isOpen = false;
    this.selectedValue = this.hiddenFieldTarget.value || "";
    this.allOptions = this.buildOptionsFromData();

    // Set initial display value
    if (this.selectedValue) {
      const selectedOption = this.allOptions.find(
        (opt) => opt.value === this.selectedValue
      );
      if (selectedOption) {
        this.inputTarget.value = selectedOption.label;
      }
    }

    // Close dropdown when clicking outside
    this.handleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener("click", this.handleClickOutside);
  }

  disconnect() {
    document.removeEventListener("click", this.handleClickOutside);
  }

  buildOptionsFromData() {
    // Try base64 encoded data first (safer for complex JSON)
    const base64Data = this.element.dataset.searchableSelectOptionsBase64;
    if (base64Data) {
      try {
        const jsonString = atob(base64Data);
        return JSON.parse(jsonString);
      } catch (e) {
        console.error("Error parsing base64 options:", e);
        return [];
      }
    }

    // Fallback to regular JSON data attribute
    const optionsData = this.element.dataset.searchableSelectOptions;
    if (optionsData) {
      return JSON.parse(optionsData);
    }
    return [];
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.closeDropdown();
    }
  }

  onInputFocus() {
    this.openDropdown();
    this.filterOptions();
  }

  onInputChange() {
    this.openDropdown();
    this.filterOptions();
  }

  filterOptions() {
    const query = this.inputTarget.value.toLowerCase().trim();
    const filteredOptions = query
      ? this.allOptions.filter(
          (opt) =>
            opt.label.toLowerCase().includes(query) ||
            opt.value.toLowerCase().includes(query)
        )
      : this.allOptions;

    this.renderOptions(filteredOptions);
  }

  renderOptions(options) {
    if (options.length === 0) {
      this.optionsTarget.innerHTML = `
        <div class="px-3 py-2 text-gray-500 text-sm">No currencies found</div>
      `;
      return;
    }

    this.optionsTarget.innerHTML = options
      .map(
        (opt) => `
        <button type="button"
                class="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none ${
                  opt.value === this.selectedValue
                    ? "bg-brand-50 text-brand-700"
                    : "text-gray-900"
                }"
                data-action="click->searchable-select#selectOption"
                data-value="${opt.value}"
                data-label="${opt.label}">
          ${opt.label}
        </button>
      `
      )
      .join("");
  }

  selectOption(event) {
    const value = event.currentTarget.dataset.value;
    const label = event.currentTarget.dataset.label;

    this.selectedValue = value;
    this.hiddenFieldTarget.value = value;
    this.inputTarget.value = label;

    this.closeDropdown();

    // Dispatch change event for form validation
    this.hiddenFieldTarget.dispatchEvent(
      new Event("change", { bubbles: true })
    );

    // Also dispatch input event on the visible input for any listeners
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }));
  }

  openDropdown() {
    if (!this.isOpen) {
      this.isOpen = true;
      this.dropdownTarget.classList.remove("hidden");
    }
  }

  closeDropdown() {
    if (this.isOpen) {
      this.isOpen = false;
      this.dropdownTarget.classList.add("hidden");

      // Restore the selected label if user didn't select anything
      if (this.selectedValue) {
        const selectedOption = this.allOptions.find(
          (opt) => opt.value === this.selectedValue
        );
        if (selectedOption) {
          this.inputTarget.value = selectedOption.label;
        }
      } else {
        this.inputTarget.value = "";
      }
    }
  }

  clearSelection() {
    this.selectedValue = "";
    this.hiddenFieldTarget.value = "";
    this.inputTarget.value = "";
    this.hiddenFieldTarget.dispatchEvent(
      new Event("change", { bubbles: true })
    );
  }
}
